import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { DateValue } from '@react-types/calendar';
import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DatePicker,
} from '@rematter/ui-web';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface LoadMarkAsReceivedDialogProps {
  onMarkAsReceived: (receivedDate: CalendarDate | undefined) => Promise<void>;
  onCancel?: () => void;
  receivedDate?: CalendarDate | undefined;
}

export const LoadMarkAsReceivedDialog: React.FC<
  LoadMarkAsReceivedDialogProps
> = ({ onMarkAsReceived, onCancel, receivedDate: initialReceivedDate }) => {
  const t = useTranslations();
  const [receivedDate, setReceivedDate] = React.useState<DateValue | null>(
    (initialReceivedDate as unknown as DateValue) || null,
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onMarkAsReceived(
        receivedDate
          ? new CalendarDate(
              receivedDate.year,
              receivedDate.month,
              receivedDate.day,
            )
          : undefined,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = receivedDate;

  return (
    <DialogOverlay isOpen className="@LoadMarkAsReceivedDialog">
      <DialogContent>
        <DialogHeader>
          <div className="text-base font-semibold">
            {t.loadStatusTransition.markAsReceivedPopover.markAsReceived}
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-4">
          <DatePicker
            value={receivedDate}
            onChange={setReceivedDate}
            className="w-full"
            label={t.loadStatusTransition.markAsReceivedPopover.receivedDate}
            isRequired={true}
          />
          <div className="flex justify-end gap-2 py-4">
            <Button variant="text" color="neutral" onPress={onCancel}>
              {t.loadStatusTransition.markAsReceivedPopover.discard}
            </Button>
            <Button
              variant="solid"
              onPress={handleConfirm}
              isLoading={isLoading}
              isDisabled={!isFormValid}
            >
              {t.loadStatusTransition.markAsReceivedPopover.markAsReceived}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
